import React from 'react';
import cn from '@/utils/cn';
import { ArrowUpOutlineIcon } from '@/components/Icons';

interface PaymentAvatarProps {
  /**
   * The icon content to place inside the circular frame - fully owned by the caller (see each
   * kind's `renderAvatar` in data/paymentPresentationStrategyExtensions.ts), so this component has
   * no idea what `TPaymentRowKind` even is. Sized/positioned as a % of the frame (Figma px ÷ 30),
   * so the whole thing scales with `size` instead of being pinned to a 30px box.
   */
  children: React.ReactNode;
  /** Avatar diameter in px. Everything inside scales proportionally, so this is the only size knob. */
  size?: number;
  /** Frame background - defaults to the initials/batch avatars' pale teal. */
  background?: string;
  /**
   * The small teal "sent" arrow overlay in the bottom-right corner. Every kind shows it except a
   * scheduled transfer's plain icon (see scheduledTransferPresentationStrategy's renderAvatar).
   */
  showBadge?: boolean;
}

const PaymentAvatar: React.FC<PaymentAvatarProps> = ({
  children,
  size = 30,
  background = 'bg-emerge-teal-3',
  showBadge = true
}) => (
  <div className="relative inline-block" style={{ width: size, height: size }}>
    <div className={cn('relative h-full w-full overflow-hidden rounded-full', background)}>
      {children}
    </div>

    {showBadge && (
      <div className="absolute bottom-0 right-0 z-10 flex h-[32%] w-[32%] items-center justify-center rounded-full bg-emerge-teal">
        <div className="h-[48%] w-[48%]">
          <ArrowUpOutlineIcon className="h-full w-full text-emerge-navy" />
        </div>
      </div>
    )}
  </div>
);

export default PaymentAvatar;
